import { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderOpen,
  FileText,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  Users,
  FileEdit,
  Tag,
  User,
  Briefcase,
  Building2,
  Shield,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLogout } from "@/hooks/api/use-auth";
import { useSiteSettings } from "@/hooks/api/use-site-settings";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AccountStatusBanner from "@/components/AccountStatusBanner";
import { AccountStatusProvider } from "@/contexts/AccountStatusContext";
import { getImageUrl } from "@/lib/image-utils";
import { useMemo } from "react";

const DashboardLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();
  const logoutMutation = useLogout();
  const isAdmin = user?.type === 'admin';
  const { data: siteSettings, isLoading: isLoadingSettings } = useSiteSettings();

  // Use logo from API or fallback to local asset
  // Add timestamp for cache busting when logo changes
  const logoSrc = useMemo(() => {
    return getImageUrl(siteSettings?.data?.logo);
  }, [siteSettings?.data?.logo]);
  const siteName = isRTL 
    ? (siteSettings?.data?.site_name_ar || 'منصة المناقصات')
    : (siteSettings?.data?.site_name || 'Tender Platform');

  const clientLinks = [
    { href: "/dashboard", label: t('sidebar.dashboard'), icon: LayoutDashboard },
    { href: "/dashboard/portfolio", label: t('sidebar.portfolio'), icon: FolderOpen },
    { href: "/dashboard/tenders", label: t('sidebar.tenders'), icon: FileText },
    { href: "/dashboard/applications", label: t('sidebar.applications'), icon: FileText },
  ];

  const adminLinks = [
    { href: "/admin/dashboard", label: t('sidebar.dashboard'), icon: LayoutDashboard },
    { href: "/admin/dashboard/clients", label: t('sidebar.clients'), icon: Users },
    { href: "/admin/dashboard/tenders", label: t('sidebar.tenders'), icon: FileText },
    { href: "/admin/dashboard/offers", label: t('sidebar.offers'), icon: MessageSquare },
    { href: "/admin/dashboard/company-projects", label: t('sidebar.companyProjects') || 'Company Projects', icon: Building2 },
    { href: "/admin/dashboard/client-projects", label: t('sidebar.clientProjects') || 'Client Projects', icon: Briefcase },
    { href: "/admin/dashboard/content", label: t('sidebar.contentManagement'), icon: FileEdit },
    { href: "/admin/dashboard/specializations", label: t('sidebar.specializations'), icon: Tag },
    { href: "/admin/dashboard/settings", label: t('sidebar.settings'), icon: Settings },
  ];

  // Admin Users link only for Super Admins
  const superAdminOnlyLinks = [
    { href: "/admin/dashboard/admins", label: t('sidebar.adminUsers') || 'Admin Users', icon: Shield },
  ];

  // Combine links based on user role
  const sidebarLinks = isAdmin
    ? (user?.is_super_admin
        ? [...adminLinks.slice(0, -1), ...superAdminOnlyLinks, adminLinks[adminLinks.length - 1]]
        : adminLinks)
    : clientLinks;

  return (
    <AccountStatusProvider>
      <div className="min-h-screen bg-muted/30 flex" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-50 w-64 bg-card ${isRTL ? 'border-l' : 'border-r'} border-border transform transition-transform duration-300 lg:translate-x-0 ${
            isSidebarOpen ? "translate-x-0" : isRTL ? "translate-x-full" : "-translate-x-full"
          } lg:static`}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-6 flex items-center justify-center">
              <Link to={isAdmin ? "/admin/dashboard" : "/dashboard"} className="flex items-center gap-3">
                {isLoadingSettings || !logoSrc ? (
                  <div className="h-10 w-32 animate-pulse bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] rounded-lg" 
                       style={{ animation: 'shimmer 1.5s infinite' }} />
                ) : (
                  <img src={logoSrc} alt={siteName} className="h-10 w-auto" />
                )}
                <span className="text-xl font-bold text-foreground">
                  {siteName}
                </span>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
              {sidebarLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-lg"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{link.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-foreground/50 z-40 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-screen">
          {/* Header */}
          <header className="bg-card border-b border-border px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 hover:bg-muted rounded-lg"
                onClick={() => setIsSidebarOpen(true)}
              >
                <Menu size={24} />
              </button>
            </div>
            
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <div className="flex items-center gap-3">
                <div className={`hidden sm:block ${isRTL ? 'text-left' : 'text-right'}`}>
                  <p className="font-semibold text-foreground text-sm">
                    {user?.name || (isRTL ? 'مستخدم' : 'User')}
                  </p>
                  {/* <p className="text-muted-foreground text-xs capitalize">
                    {user?.client?.status ? t(`status.${user.client.status}`) || user.client.status : (isRTL ? 'عميل' : 'Client')}
                  </p> */}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold border-2 border-primary hover:bg-primary/30 transition-colors">
                      {user?.name?.charAt(0)?.toUpperCase() || (isRTL ? 'ع' : 'U')}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align={isRTL ? "start" : "end"} className="w-48">
                    <DropdownMenuItem onClick={() => {
                      const profilePath = user?.type === 'admin' 
                        ? '/admin/dashboard/profile' 
                        : '/dashboard/profile';
                      navigate(profilePath);
                    }}>
                      <User className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                      <span>{t('nav.profile') || 'الملف الشخصي'}</span>
                    </DropdownMenuItem>
                    {user?.type === 'admin' && (
                      <DropdownMenuItem onClick={() => {
                        const settingsPath = '/admin/dashboard/settings';
                        navigate(settingsPath);
                      }}>
                        <Settings className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                        <span>{t('nav.settings') || 'الإعدادات'}</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={() => logoutMutation.mutate()}
                      className="text-destructive"
                    >
                      <LogOut className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                      <span>{t('nav.logout') || 'تسجيل الخروج'}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 p-6 overflow-y-auto">
            {/* Account Status Banner - Only for client users */}
            {user?.type === 'client' && <AccountStatusBanner />}
            <Outlet />
          </main>
        </div>
      </div>
    </AccountStatusProvider>
  );
};

export default DashboardLayout;
